import { t, tDynamic } from '@glory/i18n';
import {
  type RequisiteIssue,
  validateBik,
  validateCorrespondentAccount,
  validateInn,
  validateKpp,
  validateOgrn,
  validateSettlementAccount,
} from '@glory/kernel';
import { useState } from 'react';

import { Field, type FieldPart, splitField } from './field.tsx';
import { SuggestInput } from './suggest-input.tsx';
import { TextInput } from './text-fields.tsx';

/** Организация или ИП из подсказок: всё, что нужно для реквизитов. */
export interface CompanySuggestion {
  readonly inn: string;
  readonly kpp: string | null;
  readonly ogrn: string | null;
  /** Краткое название с формой: «ООО „Ромашка“». */
  readonly name: string;
  readonly fullName: string | null;
  readonly address: string | null;
  readonly director: { readonly name: string; readonly post: string | null } | null;
  readonly kind: 'legal' | 'individual';
  /** Действует (не ликвидирована, не в процессе ликвидации). */
  readonly active: boolean;
}

export interface BankSuggestion {
  readonly bic: string;
  readonly name: string;
  readonly correspondentAccount: string | null;
  readonly address: string | null;
}

export interface AddressSuggestion {
  readonly value: string;
  readonly postalCode: string | null;
}

type Loader<T> = (query: string, signal: AbortSignal) => Promise<readonly T[]>;

const digits = (value: string) => value.replace(/\D/g, '');
const issueText = (issue: RequisiteIssue) => tDynamic(`validation.${issue}`);

/** Проверка после ухода с поля: пока печатают — без ошибки, начали править — ошибка исчезает. */
function useLeaveCheck(check: () => string | null) {
  const [left, setLeft] = useState(false);
  return {
    error: left ? check() : null,
    onBlur: () => {
      setLeft(true);
    },
    touch: () => {
      setLeft(false);
    },
  };
}

export interface CompanyFieldProps extends FieldPart {
  /** ИНН. */
  readonly value: string;
  readonly onValueChange: (inn: string) => void;
  /** Подсказки по названию или ИНН; без них поле проверяет только контрольное число. */
  readonly loadSuggestions?: Loader<CompanySuggestion> | undefined;
  /** Выбрали компанию: заполнить КПП, ОГРН, адрес, руководителя. */
  readonly onPick?: (company: CompanySuggestion) => void;
}

/** ИНН / компания: ищет по названию или ИНН, значение — ИНН с проверкой контрольного числа. */
export function CompanyField(props: CompanyFieldProps) {
  const [field, { value, onValueChange, loadSuggestions, onPick }] = splitField(props);
  const [picked, setPicked] = useState<string | null>(null);
  const check = useLeaveCheck(() => {
    if (value.trim() === '') return null;
    if (/[^\d\s]/.test(value)) return t('fields.companyPickOrInn');
    const issue = validateInn(digits(value));
    return issue ? issueText(issue) : null;
  });
  return (
    <Field
      {...field}
      error={field.error ?? check.error}
      description={picked ?? field.description ?? (loadSuggestions ? t('fields.companyHint') : undefined)}
    >
      <SuggestInput<CompanySuggestion>
        value={value}
        onValueChange={(next) => {
          check.touch();
          setPicked(null);
          onValueChange(next);
        }}
        loadSuggestions={loadSuggestions}
        getKey={(c) => `${c.inn}-${c.kpp ?? ''}`}
        describe={(c) => ({
          label: c.name,
          description: [
            t('fields.innShort', { inn: c.inn }),
            c.kpp ? t('fields.kppShort', { kpp: c.kpp }) : null,
            c.active ? c.address : t('fields.companyInactive'),
          ]
            .filter(Boolean)
            .join(' · '),
        })}
        onPick={(c) => {
          check.touch();
          setPicked(c.name);
          onValueChange(c.inn);
          onPick?.(c);
        }}
        onBlur={check.onBlur}
        placeholder={loadSuggestions ? t('fields.companyPlaceholder') : t('fields.innPlaceholder')}
      />
    </Field>
  );
}

export interface BankFieldProps extends FieldPart {
  /** БИК. */
  readonly value: string;
  readonly onValueChange: (bic: string) => void;
  readonly loadSuggestions?: Loader<BankSuggestion> | undefined;
  /** Выбрали банк: заполнить корреспондентский счёт и название. */
  readonly onPick?: (bank: BankSuggestion) => void;
}

/** Банк: поиск по БИК или названию, значение — БИК. */
export function BankField(props: BankFieldProps) {
  const [field, { value, onValueChange, loadSuggestions, onPick }] = splitField(props);
  const [picked, setPicked] = useState<string | null>(null);
  const check = useLeaveCheck(() => {
    if (value.trim() === '') return null;
    if (/[^\d\s]/.test(value)) return t('fields.bankPickOrBic');
    const issue = validateBik(digits(value));
    return issue ? issueText(issue) : null;
  });
  return (
    <Field {...field} error={field.error ?? check.error} description={picked ?? field.description}>
      <SuggestInput<BankSuggestion>
        value={value}
        onValueChange={(next) => {
          check.touch();
          setPicked(null);
          onValueChange(next);
        }}
        loadSuggestions={loadSuggestions}
        getKey={(b) => b.bic}
        describe={(b) => ({
          label: b.name,
          description: [
            t('fields.bicShort', { bic: b.bic }),
            b.correspondentAccount ? t('fields.corrShort', { account: b.correspondentAccount }) : null,
          ]
            .filter(Boolean)
            .join(' · '),
        })}
        onPick={(b) => {
          check.touch();
          setPicked(b.name);
          onValueChange(b.bic);
          onPick?.(b);
        }}
        onBlur={check.onBlur}
        inputMode={loadSuggestions ? 'text' : 'numeric'}
        placeholder={loadSuggestions ? t('fields.bankPlaceholder') : t('fields.bicPlaceholder')}
      />
    </Field>
  );
}

export interface AccountFieldProps extends FieldPart {
  readonly value: string;
  readonly onValueChange: (account: string) => void;
  /** БИК банка: счёт проверяется контрольным ключом вместе с ним. */
  readonly bic: string;
  readonly kind?: 'settlement' | 'correspondent';
}

/** Расчётный или корреспондентский счёт: 20 цифр, ключ сверяется с БИК. */
export function AccountField(props: AccountFieldProps) {
  const [field, { value, onValueChange, bic, kind = 'settlement' }] = splitField(props);
  const check = useLeaveCheck(() => {
    const account = digits(value);
    if (account === '') return null;
    // Пока БИК неверный или не заполнен — проверяем только длину.
    const issue =
      validateBik(digits(bic)) !== null
        ? account.length === 20
          ? null
          : 'account_format'
        : kind === 'settlement'
          ? validateSettlementAccount(account, digits(bic))
          : validateCorrespondentAccount(account, digits(bic));
    return issue ? issueText(issue) : null;
  });
  return (
    <Field {...field} error={field.error ?? check.error}>
      <TextInput
        value={value}
        onValueChange={(next) => {
          check.touch();
          onValueChange(digits(next).slice(0, 20));
        }}
        onBlur={check.onBlur}
        inputMode="numeric"
        autoComplete="off"
        className="tabular-nums"
        placeholder={t('fields.accountPlaceholder')}
      />
    </Field>
  );
}

export interface RequisiteFieldProps extends FieldPart {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly kind: 'kpp' | 'ogrn';
}

const REQUISITES = {
  kpp: { validate: validateKpp, placeholder: '773601001', max: 9 },
  ogrn: { validate: validateOgrn, placeholder: '1027700132195', max: 15 },
} as const;

/** КПП или ОГРН / ОГРНИП с проверкой формата и контрольного числа. */
export function RequisiteField(props: RequisiteFieldProps) {
  const [field, { value, onValueChange, kind }] = splitField(props);
  const spec = REQUISITES[kind];
  const check = useLeaveCheck(() => {
    const clean = value.replace(/\s/g, '').toUpperCase();
    if (clean === '') return null;
    const issue = spec.validate(clean);
    return issue ? issueText(issue) : null;
  });
  return (
    <Field {...field} error={field.error ?? check.error}>
      <TextInput
        value={value}
        onValueChange={(next) => {
          check.touch();
          onValueChange(next.replace(/\s/g, '').toUpperCase().slice(0, spec.max));
        }}
        onBlur={check.onBlur}
        inputMode={kind === 'ogrn' ? 'numeric' : 'text'}
        autoComplete="off"
        className="tabular-nums"
        placeholder={spec.placeholder}
      />
    </Field>
  );
}

export interface AddressFieldProps extends FieldPart {
  readonly value: string;
  readonly onValueChange: (address: string) => void;
  readonly loadSuggestions?: Loader<AddressSuggestion> | undefined;
  readonly onPick?: (address: AddressSuggestion) => void;
}

/** Адрес: подсказки по мере ввода, но можно написать и свой. */
export function AddressField(props: AddressFieldProps) {
  const [field, { value, onValueChange, loadSuggestions, onPick }] = splitField(props);
  return (
    <Field {...field}>
      <SuggestInput<AddressSuggestion>
        value={value}
        onValueChange={onValueChange}
        loadSuggestions={loadSuggestions}
        minQueryLength={3}
        getKey={(a) => a.value}
        describe={(a) => ({ label: a.value, description: a.postalCode ?? undefined })}
        onPick={(a) => {
          onValueChange(a.value);
          onPick?.(a);
        }}
        placeholder={t('fields.addressPlaceholder')}
      />
    </Field>
  );
}

/** ФИО из подсказок: целиком и по частям. */
export interface FioSuggestion {
  readonly value: string;
  readonly surname: string | null;
  readonly name: string | null;
  readonly patronymic: string | null;
  readonly gender: 'male' | 'female' | null;
}

/** Подразделение, выдавшее паспорт. */
export interface FmsUnitSuggestion {
  readonly code: string;
  readonly name: string;
}

export interface FioFieldProps extends FieldPart {
  readonly value: string;
  readonly onValueChange: (name: string) => void;
  readonly loadSuggestions?: Loader<FioSuggestion> | undefined;
  readonly placeholder?: string | undefined;
  readonly maxLength?: number | undefined;
  readonly autoFocus?: boolean | undefined;
}

/** ФИО: подсказки фамилии, имени и отчества по мере ввода, но можно написать и своё. */
export function FioField(props: FioFieldProps) {
  const [field, { value, onValueChange, loadSuggestions, placeholder, maxLength, autoFocus }] =
    splitField(props);
  return (
    <Field {...field}>
      <SuggestInput<FioSuggestion>
        value={value}
        onValueChange={(next) => {
          onValueChange(maxLength === undefined ? next : next.slice(0, maxLength));
        }}
        loadSuggestions={loadSuggestions}
        getKey={(f) => f.value}
        describe={(f) => ({ label: f.value })}
        onPick={(f) => {
          onValueChange(f.value);
        }}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />
    </Field>
  );
}

export interface DivisionCodeFieldProps extends FieldPart {
  /** Код подразделения: «772-053». */
  readonly value: string;
  readonly onValueChange: (code: string) => void;
  readonly loadSuggestions?: Loader<FmsUnitSuggestion> | undefined;
  /** Выбрали подразделение: заполнить «Кем выдан». */
  readonly onPick?: (unit: FmsUnitSuggestion) => void;
  readonly placeholder?: string | undefined;
}

/** Код подразделения паспорта: по коду DaData подсказывает, кем выдан паспорт. */
export function DivisionCodeField(props: DivisionCodeFieldProps) {
  const [field, { value, onValueChange, loadSuggestions, onPick, placeholder }] = splitField(props);
  return (
    <Field
      {...field}
      description={field.description ?? (loadSuggestions ? t('fields.divisionHint') : undefined)}
    >
      <SuggestInput<FmsUnitSuggestion>
        value={value}
        onValueChange={onValueChange}
        loadSuggestions={loadSuggestions}
        minQueryLength={3}
        inputMode="numeric"
        getKey={(u) => `${u.code}-${u.name}`}
        describe={(u) => ({ label: u.name, description: u.code })}
        onPick={(u) => {
          onValueChange(u.code);
          onPick?.(u);
        }}
        placeholder={placeholder}
      />
    </Field>
  );
}
